import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FilterList, LockOutlined, Menu as MenuIcon, Refresh } from '@mui/icons-material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import TvIcon from '@mui/icons-material/Tv';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useNavigate } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { screensApi } from './Screens.api';
import { MANAGE_SCREENS_PATH, Screen, cachePercent, formatAgo } from './Screens.types';
import { ScreenCredentialsPanel } from './ScreenCredentialsPanel';
import { UpgradeDialog } from './UpgradeDialog';
import { screenDialogBodySx } from './dialogSx';

const REFRESH_MS = 10000;
const VIEWMODE_STORAGE_KEY = 'manage-screens-view-mode';

type ScreenViewMode = 'tile' | 'table';
type ScreenSortField = 'name' | 'lastSeenAt' | 'status' | 'playlist' | 'version' | 'updatedAt';
type ScreenSortOrder = 'asc' | 'desc';

const compareScreens = (a: Screen, b: Screen, field: ScreenSortField, order: ScreenSortOrder) => {
    const dir = order === 'asc' ? 1 : -1;
    const value = (s: Screen): string | number => {
        switch (field) {
            case 'name':
                return s.name.toLowerCase();
            case 'status':
                return s.online ? 1 : 0;
            case 'playlist':
                return (s.playlistName || '').toLowerCase();
            case 'version':
                return s.deviceStatus?.clientVersion || '';
            case 'updatedAt':
                return s.updatedAt;
            default:
                return s.lastSeenAt || '';
        }
    };
    const av = value(a);
    const bv = value(b);
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return a.name.localeCompare(b.name);
};

const CacheCell: React.FC<{ screen: Screen }> = ({ screen }) => {
    const d = screen.deviceStatus;
    if (!d) return <>—</>;
    if (d.syncState === 'caching') {
        const pct = cachePercent(d.progress);
        return (
            <Box sx={{ minWidth: 120 }}>
                <Typography variant="caption">
                    Caching {pct}% · {(d.progress?.reelsTotal ?? 0) > 0 ? `${d.progress?.reelsDone ?? 0}/${d.progress?.reelsTotal} reels` : `${d.progress?.filesDone ?? 0}/${d.progress?.filesTotal ?? 0}`}
                </Typography>
                <LinearProgress variant="determinate" value={pct} color="secondary" />
            </Box>
        );
    }
    if (d.syncState === 'cached') {
        const stale = screen.playlistId && (d.cachedPlaylistId !== screen.playlistId || d.cachedRevision !== screen.playlistRevision);
        return <Chip size="small" color={stale ? 'warning' : 'success'} label={stale ? 'Cached (older revision)' : 'Cached'} />;
    }
    if (d.syncState === 'error') return <Chip size="small" color="error" label="Error" title={d.lastError} />;
    return <Chip size="small" variant="outlined" label="Idle" />;
};

/** Beam on Screen → Manage Screens (`/#/beamonscreen/manage-screens`). */
export const ManageScreensPage: React.FC<{ title?: string }> = ({ title = 'Manage Screens' }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const heroGradient =
        theme.palette.dashboard?.counterHeroGradient ??
        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;
    const [screens, setScreens] = useState<Screen[]>([]);
    const [onlineWindowSec, setOnlineWindowSec] = useState(30);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<ScreenSortField>('lastSeenAt');
    const [sortOrder, setSortOrder] = useState<ScreenSortOrder>('desc');
    const [viewMode, setViewMode] = useState<ScreenViewMode>(() => {
        try {
            return localStorage.getItem(VIEWMODE_STORAGE_KEY) === 'table' ? 'table' : 'tile';
        } catch {
            return 'tile';
        }
    });
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
    const [credentialsOpen, setCredentialsOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [bulkUpgradeOpen, setBulkUpgradeOpen] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const notify = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

    const load = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            const res = await screensApi.list();
            setScreens(res.screens);
            setOnlineWindowSec(res.onlineWindowSec);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load screens');
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
        const t = setInterval(() => {
            void load(true);
            setNow(Date.now());
        }, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    const changeViewMode = (next: ScreenViewMode | null) => {
        if (!next) return;
        setViewMode(next);
        try {
            localStorage.setItem(VIEWMODE_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? screens.filter((s) =>
                  [s.name, s.location, s.playlistName, s.deviceStatus?.hostname, s.deviceStatus?.ip].some((v) => (v || '').toLowerCase().includes(q)),
              )
            : screens;
        return [...filtered].sort((a, b) => compareScreens(a, b, sortField, sortOrder));
    }, [screens, search, sortField, sortOrder]);

    const allSelected = visible.length > 0 && visible.every((s) => selected.includes(s._id));
    const openScreen = (screenId: string) => navigate(`${MANAGE_SCREENS_PATH}/${screenId}`);
    const toggleSelected = (id: string) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const filterControls = (fullWidth: boolean) => (
        <>
            <FormControl sx={fullWidth ? undefined : { minWidth: 160 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as ScreenSortField)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="lastSeenAt">Last Seen</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="playlist">Playlist</MenuItem>
                    <MenuItem value="version">Version</MenuItem>
                    <MenuItem value="updatedAt">Last Updated On</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={fullWidth ? undefined : { minWidth: 100 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Order</InputLabel>
                <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as ScreenSortOrder)}>
                    <MenuItem value="asc">ASC</MenuItem>
                    <MenuItem value="desc">DSC</MenuItem>
                </Select>
            </FormControl>
        </>
    );

    const viewToggle = (
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_e, next) => changeViewMode(next)} size="small" sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}>
            <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: MANAGE_SCREENS_PATH, itemLabel: title },
                ]}
            />
            {loading && (
                <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={() => setMobileFiltersOpen(true)} size="large" aria-label="Search and filters" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                        <FilterList />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
                    <IconButton color="secondary" onClick={() => setMobileActionsOpen(true)} size="large" aria-label="Toolbar actions" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                        <MenuIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <TextField label="Search screen" variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flexGrow: 1, minWidth: 200 }} />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>{filterControls(false)}</Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    {viewToggle}
                    <Button variant="contained" color="secondary" size="small" startIcon={<SystemUpdateAltIcon />} disabled={selected.length === 0} onClick={() => setBulkUpgradeOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                        Upgrade {selected.length ? `${selected.length} ` : ''}selected
                    </Button>
                    <Tooltip title="Screen credentials & installation">
                        <Button variant="contained" color="warning" onClick={() => setCredentialsOpen(true)} size="small" sx={{ minWidth: 36, px: 1, flexShrink: 0 }} aria-label="View Credentials">
                            <LockOutlined fontSize="small" />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Refresh">
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => void load()}
                            disabled={loading}
                            size="small"
                            sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                            aria-label="Refresh"
                        >
                            <Refresh fontSize="small" />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <Dialog open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Search & Filters</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Search screen" size="small" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth />
                        {filterControls(true)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileFiltersOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={mobileActionsOpen} onClose={() => setMobileActionsOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Actions</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                        <Typography variant="body2">View:</Typography>
                        {viewToggle}
                        <Button variant="contained" color="secondary" size="small" startIcon={<SystemUpdateAltIcon />} disabled={selected.length === 0} onClick={() => { setMobileActionsOpen(false); setBulkUpgradeOpen(true); }}>
                            Upgrade selected
                        </Button>
                        <Button variant="contained" color="warning" size="small" startIcon={<LockOutlined />} onClick={() => { setMobileActionsOpen(false); setCredentialsOpen(true); }} aria-label="View Credentials">
                            Credentials
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileActionsOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            <ScreenCredentialsPanel open={credentialsOpen} onClose={() => setCredentialsOpen(false)} onMessage={notify} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && visible.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <TvIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {screens.length === 0 ? 'No screens yet' : 'No screens match your search'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {screens.length === 0
                            ? 'Install st-ck-screen-client and enter the Company ID and Secret Key from Screen credentials.'
                            : 'Try a different search.'}
                    </Typography>
                </Paper>
            )}

            {viewMode === 'tile' && visible.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                    {visible.map((s) => (
                        <Box
                            key={s._id}
                            onClick={() => openScreen(s._id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openScreen(s._id);
                                }
                            }}
                            sx={{
                                width: 300,
                                height: 200,
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                position: 'relative',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                cursor: 'pointer',
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: selected.includes(s._id) ? 'secondary.main' : 'divider',
                                '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
                            }}
                        >
                            <Box sx={{ position: 'absolute', inset: 0, background: heroGradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TvIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.85)' }} />
                            </Box>
                            <Box sx={{ position: 'absolute', top: 8, left: 8, right: 40, zIndex: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={s.online ? 'Online' : 'Offline'} color={s.online ? 'success' : 'default'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
                                <Chip label={s.deviceStatus?.clientVersion || 'no version'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)' }} />
                                <Chip label={s.deviceStatus?.resolution || 'no resolution'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)' }} />
                                {s.playlistName && <Chip label={s.playlistName} color="secondary" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, maxWidth: 140 }} />}
                            </Box>
                            <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 3 }} onClick={(e) => e.stopPropagation()}>
                                <Checkbox checked={selected.includes(s._id)} onChange={() => toggleSelected(s._id)} sx={{ color: '#fff', '&.Mui-checked': { color: 'secondary.main' } }} inputProps={{ 'aria-label': `Select ${s.name}` }} />
                            </Box>
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, px: 1.5, py: 1.25, background: 'linear-gradient(to top, rgba(0,0,0,0.88), rgba(0,0,0,0.2))' }}>
                                <Typography variant="subtitle2" noWrap sx={{ color: '#fff', fontWeight: 700 }}>{s.name}</Typography>
                                <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>
                                    {[s.deviceStatus?.resolution, s.location || s.deviceStatus?.ip || 'No location', formatAgo(s.lastSeenAt, now)].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {viewMode === 'table' && visible.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={!allSelected && visible.some((s) => selected.includes(s._id))}
                                        onChange={() => setSelected(allSelected ? [] : visible.map((s) => s._id))}
                                    />
                                </TableCell>
                                <TableCell>Screen</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Playlist</TableCell>
                                <TableCell>Reels</TableCell>
                                <TableCell>Cache</TableCell>
                                <TableCell>Now playing</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Resolution</TableCell>
                                    <TableCell>IP</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((s) => (
                                <TableRow key={s._id} hover sx={{ cursor: 'pointer' }} onClick={() => openScreen(s._id)}>
                                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selected.includes(s._id)}
                                            onChange={() => setSelected((sel) => (sel.includes(s._id) ? sel.filter((x) => x !== s._id) : [...sel, s._id]))}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {s.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.location || s.deviceStatus?.hostname || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={`Online means a poll within the last ${onlineWindowSec}s`}>
                                            <Chip size="small" color={s.online ? 'success' : 'default'} label={s.online ? 'Online' : 'Offline'} />
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {formatAgo(s.lastSeenAt, now)}
                                        </Typography>
                                        {s.status === 'factory-reset-pending' && <Chip size="small" color="warning" label="Reset pending" sx={{ mt: 0.5 }} />}
                                    </TableCell>
                                    <TableCell>{s.playlistName || '—'}</TableCell>
                                    <TableCell>{s.deviceStatus?.reelsCount ?? 0}</TableCell>
                                    <TableCell>
                                        <CacheCell screen={s} />
                                    </TableCell>
                                    <TableCell>
                                        {s.nowPlaying?.name ? (
                                            <>
                                                <Typography variant="body2">
                                                    {s.nowPlaying.name} v{s.nowPlaying.version}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatAgo(s.nowPlaying.startedAt, now)}
                                                </Typography>
                                            </>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                        {s.deviceStatus?.clientVersion || '—'}
                                        {s.upgradePending && (
                                            <Typography variant="caption" color="secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                                → {s.targetClientVersion} {s.deviceStatus?.upgradeState && s.deviceStatus.upgradeState !== 'none' ? `(${s.deviceStatus.upgradeState})` : ''}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{s.deviceStatus?.resolution || '—'}</TableCell>
                                    <TableCell>{s.deviceStatus?.ip || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <UpgradeDialog
                open={bulkUpgradeOpen}
                screenNames={screens.filter((s) => selected.includes(s._id)).map((s) => s.name)}
                onClose={() => setBulkUpgradeOpen(false)}
                onConfirm={async (version) => {
                    const results = await screensApi.sendBulkCommand(selected, 'upgrade', { targetVersion: version });
                    const ok = results.filter((r) => r.commandId).length;
                    setBulkUpgradeOpen(false);
                    notify(`Upgrade to ${version} scheduled on ${ok} screen(s)`);
                    void load(true);
                }}
            />

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ManageScreensPage;
